import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import PrimaryButton from '../../components/common/PrimaryButton';
import InputField from '../../components/common/InputField';
import { colors, typography } from '../../styles/theme/index';
import storage from '../../utils/storage';
import {
  Form,
  FormContainer,
  FormFields,
  ButtonContainer,
  CompanyDisplay,
  CompanyValue,
  AutocompleteDropdown,
  AutocompleteItem,
  Label,
  FormSection,
  FlexRow
} from '../../styles/components';
import { insurers } from '../../data/insurers';
import {
  ClaimsToggleContainer,
  ToggleButton,
  ClaimHistoryTitle,
  ClaimHistoryContainer,
  AddClaimButton,
  RemoveClaimButton,
  InputClaimsTitle,
  ClaimsList,
  ClaimItemWrapper
} from '../../components/onboarding/ClaimsSection.styles';

const OnboardingStep8 = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [formData, setFormData] = useState({
    currentInsurer: '',
    startDate: '',
    hasClaims: false,
    claims: [],
    newClaim: { date: '', amount: '' }
  });
  const [filteredInsurers, setFilteredInsurers] = useState([]);
  const dropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load company name from step 3
    const step3Data = storage.getItem('step3Data');
    if (step3Data) {
      const { companyName } = JSON.parse(step3Data);
      setCompanyName(companyName);
    }

    // Load previously saved insurance details
    const step8Data = storage.getItem('step8Data');
    if (step8Data) {
      const savedData = JSON.parse(step8Data);
      setFormData(prev => ({
        ...prev,
        ...savedData,
        newClaim: { date: '', amount: '' }
      }));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilteredInsurers([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInsurerChange = (e) => {
    const { value } = e.target;
    
    const newFormData = {
      ...formData,
      currentInsurer: value  // Allow any input value
    };
    setFormData(newFormData);
    storage.setItem('step8Data', JSON.stringify(newFormData));
    
    if (value.length > 0) {
      const filtered = insurers.filter(insurer =>
        insurer.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredInsurers(filtered);
    } else {
      setFilteredInsurers([]);
    }
  };

  const handleInsurerSelect = (insurer) => {
    const newFormData = {
      ...formData,
      currentInsurer: insurer  // Use exact value from dropdown
    };
    setFormData(newFormData);
    storage.setItem('step8Data', JSON.stringify(newFormData));
    setFilteredInsurers([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    storage.setItem('step8Data', JSON.stringify(newFormData));
  };

  const toggleClaims = (value) => {
    const newFormData = {
      ...formData,
      hasClaims: value,
      claims: value ? formData.claims : [],
      newClaim: { date: '', amount: '' }
    };
    setFormData(newFormData);
    storage.setItem('step8Data', JSON.stringify({
      ...newFormData,
      newClaim: undefined
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.currentInsurer || !formData.startDate) {
        throw new Error('Please fill in all required fields');
      }

      // Get stored Pipedrive data
      const pipedriveData = storage.getItem('pipedriveData');
      if (!pipedriveData) {
        throw new Error('No Pipedrive data found. Please restart the onboarding process.');
      }

      const { organizationId, dealId } = JSON.parse(pipedriveData);
      if (!organizationId || !dealId) {
        throw new Error('No organization ID or deal ID found. Please restart the onboarding process.');
      }

      // Load data from previous steps for the summary
      const step3Data = storage.getItem('step3Data');
      const step4Data = storage.getItem('step4Data');
      const step6Data = storage.getItem('step6Data');
      const step7Data = storage.getItem('step7Data');

      // Prepare the data for submission
      const submitData = {
        organizationId,
        dealId,
        currentInsurer: formData.currentInsurer,
        insuranceStartDate: formData.startDate,
        hasClaims: formData.hasClaims,
        claims: formData.hasClaims ? formData.claims : [],
        // Include data from previous steps for the summary
        companyDetails: step3Data ? JSON.parse(step3Data) : null,
        organizationDetails: step4Data ? JSON.parse(step4Data) : null,
        selectedCovers: step6Data ? JSON.parse(step6Data).selectedCovers : [],
        coverLimits: step7Data ? JSON.parse(step7Data).coverLimits : null,
        properties: step7Data ? JSON.parse(step7Data).properties : []
      };

      // Submit all data to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-eight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to submit insurance details');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit insurance details');
      }

      // Store form data
      storage.setItem('step8Data', JSON.stringify({
        ...formData,
        newClaim: undefined
      }));

      // Navigate to thank you page
      navigate('/thank-you');
    } catch (error) {
      console.error('Error in form submission:', error);
      setError(error.message || 'Failed to save your data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Store current form data before navigating back
    const dataToStore = {
      ...formData,
      newClaim: undefined // Don't store the newClaim field
    };
    storage.setItem('step8Data', JSON.stringify(dataToStore));
    
    // Don't clear any data, just navigate back
    navigate('/onboarding/step-seven');
  };

  const handleNewClaimChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      newClaim: {
        ...prev.newClaim,
        [field]: value
      }
    }));
  };

  const handleAddClaim = (e) => {
    e.preventDefault();
    if (formData.newClaim?.date && formData.newClaim?.amount) {
      const newFormData = {
        ...formData,
        claims: [...formData.claims, formData.newClaim],
        newClaim: { date: '', amount: '' }
      };
      setFormData(newFormData);
      storage.setItem('step8Data', JSON.stringify({
        ...newFormData,
        newClaim: undefined
      }));
    }
  };

  const handleRemoveClaim = (index) => {
    const newFormData = {
      ...formData,
      claims: formData.claims.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    storage.setItem('step8Data', JSON.stringify(newFormData));
  };

  return (
    <OnboardingLayout
      title="Your"
      brandText="Insurance Details"
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{companyName}</CompanyValue>
        </CompanyDisplay>

        <Form onSubmit={handleSubmit}>
          <FormFields>
            <div style={{ position: 'relative' }}>
              <InputField
                label="Current Insurer"
                name="currentInsurer"
                type="text"
                value={formData.currentInsurer}
                onChange={handleInsurerChange}
                placeholder="Enter your current insurer"
                required
              />
              {filteredInsurers.length > 0 && (
                <AutocompleteDropdown ref={dropdownRef}>
                  {filteredInsurers.map((insurer) => (
                    <AutocompleteItem
                      key={insurer}
                      onClick={() => handleInsurerSelect(insurer)}
                    >
                      {insurer}
                    </AutocompleteItem>
                  ))}
                </AutocompleteDropdown>
              )}
            </div>

            <InputField
              label="Start Date"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
              required
            />

            <Label>Have you made claims within the last 5 years?</Label>
            <ClaimsToggleContainer>
              <ToggleButton
                type="button"
                selected={formData.hasClaims}
                onClick={() => toggleClaims(true)}
              >
                Yes
              </ToggleButton>
              <ToggleButton
                type="button"
                selected={!formData.hasClaims}
                onClick={() => toggleClaims(false)}
              >
                No
              </ToggleButton>
            </ClaimsToggleContainer>

            {formData.hasClaims && (
              <>
                <ClaimHistoryTitle>Claim History</ClaimHistoryTitle>
                
                <FlexRow>
                  <InputField
                    label="Date of claim"
                    type="date"
                    value={formData.newClaim?.date || ''}
                    onChange={(e) => handleNewClaimChange('date', e.target.value)}
                  />
                  <InputField
                    label="Amount"
                    type="number"
                    value={formData.newClaim?.amount || ''}
                    onChange={(e) => handleNewClaimChange('amount', e.target.value)}
                    placeholder="£"
                  />
                  <AddClaimButton 
                    type="button"
                    onClick={handleAddClaim}
                  >
                    Add Claim
                  </AddClaimButton>
                </FlexRow>

                {formData.claims.length > 0 && (
                  <ClaimsList>
                    {formData.claims.map((claim, index) => (
                      <FlexRow key={index}>
                        <ClaimItemWrapper>
                          {new Date(claim.date).toLocaleDateString('en-GB')}
                        </ClaimItemWrapper>
                        <ClaimItemWrapper>
                          £{claim.amount}
                        </ClaimItemWrapper>
                        <RemoveClaimButton
                          type="button"
                          onClick={() => handleRemoveClaim(index)}
                        >
                          Remove Claim
                        </RemoveClaimButton>
                      </FlexRow>
                    ))}
                  </ClaimsList>
                )}
              </>
            )}
          </FormFields>

          <ButtonContainer>
            <PrimaryButton type="submit" disabled={isSubmitting}>Submit</PrimaryButton>
          </ButtonContainer>
        </Form>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep8; 